import { Controller } from "@hotwired/stimulus"
import { storageUsage } from "../field_image_queue"

// Displays offline storage usage on the Sync screen's storage link card.
// Shows a visual fill bar (pending images vs 200MB budget) and a nudge
// when storage is getting full.
//
// Thresholds:
//   < 70%  of budget -- bar green, no nudge
//   70–99% of budget -- bar amber, amber nudge "Running low — sync soon"
//   >= 100% (over budget) -- bar red, red nudge "Storage full — sync before capturing more"
//
// Targets:
//   summary     -- text description in the header row
//   gaugeRow    -- the row containing the bar (hidden when nothing pending)
//   pendingBar  -- the fill element inside the bar track
//   nudge       -- the entire nudge row (hidden when below threshold)
//   nudgeIcon   -- the SVG icon inside the nudge (receives colour class)
//   nudgeText   -- the text inside the nudge

export default class extends Controller {
  static targets = ["summary", "overBudgetBadge", "gaugeRow", "pendingBar", "nudge", "nudgeIcon", "nudgeText"]

  async connect() {
    try {
      const usage = await storageUsage()
      this._applyUsage(usage)
    } catch {
      if (this.hasSummaryTarget) {
        this.summaryTarget.textContent = "Unable to check"
      }
    }
  }

  _applyUsage(usage) {
    // Summary text
    if (this.hasSummaryTarget) {
      if (usage.pendingBytes === 0) {
        this.summaryTarget.textContent = "None queued"
      } else {
        this.summaryTarget.textContent = `${usage.pendingMB} MB of ${usage.softCeilingMB} MB used`
      }
    }

    // Legacy overBudgetBadge (kept for backward compat with any other usage)
    if (this.hasOverBudgetBadgeTarget) {
      const show = usage.overBudget
      this.overBudgetBadgeTarget.classList.toggle("hidden", !show)
      if (show) {
        this.overBudgetBadgeTarget.classList.add("bg-red-100", "text-red-600")
        this.overBudgetBadgeTarget.classList.remove("bg-slate-100", "text-slate-500")
      } else {
        this.overBudgetBadgeTarget.classList.add("bg-slate-100", "text-slate-500")
        this.overBudgetBadgeTarget.classList.remove("bg-red-100", "text-red-600")
      }
    }

    // Gauge bar
    if (usage.pendingBytes === 0) {
      if (this.hasGaugeRowTarget) this.gaugeRowTarget.classList.add("hidden")
      if (this.hasNudgeTarget)    this.nudgeTarget.classList.add("hidden")
      return
    }

    const fill = Math.min(usage.pendingBytes / (usage.softCeilingBytes || 200 * 1024 * 1024), 1)
    const pct  = (fill * 100).toFixed(1)

    if (this.hasGaugeRowTarget) {
      this.gaugeRowTarget.classList.remove("hidden")
    }

    if (this.hasPendingBarTarget) {
      this.pendingBarTarget.style.width = `${pct}%`
      // Colour transitions — static class names only (no dynamic Tailwind strings)
      this.pendingBarTarget.classList.remove("bg-emerald-400", "bg-amber-400", "bg-red-400")
      if (fill >= 1) {
        this.pendingBarTarget.classList.add("bg-red-400")
      } else if (fill >= 0.7) {
        this.pendingBarTarget.classList.add("bg-amber-400")
      } else {
        this.pendingBarTarget.classList.add("bg-emerald-400")
      }
    }

    // Nudge
    if (fill >= 0.7) {
      if (this.hasNudgeTarget) {
        this.nudgeTarget.classList.remove("hidden")

        // Colour the nudge row
        if (fill >= 1) {
          this.nudgeTarget.classList.add("bg-red-50", "border-red-100")
          this.nudgeTarget.classList.remove("bg-amber-50", "border-amber-100", "border-slate-50")
          if (this.hasNudgeIconTarget) {
            this.nudgeIconTarget.classList.add("text-red-500")
            this.nudgeIconTarget.classList.remove("text-amber-500")
          }
          if (this.hasNudgeTextTarget) {
            this.nudgeTextTarget.textContent = "Storage full — sync before capturing more"
            this.nudgeTextTarget.classList.add("text-red-700")
            this.nudgeTextTarget.classList.remove("text-amber-700")
          }
        } else {
          this.nudgeTarget.classList.add("bg-amber-50", "border-amber-100")
          this.nudgeTarget.classList.remove("bg-red-50", "border-red-100", "border-slate-50")
          if (this.hasNudgeIconTarget) {
            this.nudgeIconTarget.classList.add("text-amber-500")
            this.nudgeIconTarget.classList.remove("text-red-500")
          }
          if (this.hasNudgeTextTarget) {
            this.nudgeTextTarget.textContent = "Running low on photo storage — sync soon"
            this.nudgeTextTarget.classList.add("text-amber-700")
            this.nudgeTextTarget.classList.remove("text-red-700")
          }
        }
      }
    } else {
      if (this.hasNudgeTarget) this.nudgeTarget.classList.add("hidden")
    }
  }
}
